import { ReactNode, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  BarChart3,
  BookOpen,
  Building2,
  CircleUser,
  FileQuestion,
  FolderKanban,
  Gauge,
  Globe2,
  LifeBuoy,
  Lock,
  Power,
  Radar,
  Settings,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { ThemeToggle } from "../ThemeToggle";
import MissingCredentialsModal from "./MissingCredentialsModal";

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { logout, user } = useAuth();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  const navItems = [
    { path: "/v1/dashboard", label: "Dashboard", icon: Gauge },
    { path: "/v1/procurement", label: "Procurement", icon: BarChart3 },
    { path: "/v1/migration", label: "Migration", icon: Activity },
    { path: "/v1/account", label: "Account", icon: Building2 },
    { path: "/v1/web-detective", label: "Web Detective", icon: Radar },
    { path: "/v1/ray-id", label: "Ray ID", icon: FileQuestion },
    { path: "/v1/dns", label: "DNS", icon: Globe2 },
    { path: "/v1/locked-domains", label: "Locked Domains", icon: Lock },
    { path: "/v1/security", label: "Security", icon: ShieldCheck },
    { path: "/v1/workers", label: "Workers KV", icon: Zap },
    { path: "/v1/projects", label: "Projects", icon: FolderKanban },
    { path: "/v1/config", label: "Config", icon: Settings },
    { path: "/v1/documentation", label: "Documentation", icon: BookOpen },
    { path: "/v1/support", label: "Support", icon: LifeBuoy },
  ];

  return (
    <div className="flex h-screen bg-background overflow-hidden transition-colors duration-300 font-sans">
      {/* Sidebar Navigation */}
      <aside className="w-64 bg-card border-r border-border flex flex-col hidden md:flex shadow-xl z-30">
        {/* Logo Section */}
        <div className="h-16 flex items-center px-6 border-b border-border/50 bg-card/95 backdrop-blur-sm">
          <img
            src="/hub-logo-primary.png"
            alt="Hub International"
            className="h-8 w-auto object-contain" // Adjusted size
            onError={() => console.error("Logo failed")}
          />
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group border border-transparent",
                location.pathname === item.path
                  ? "bg-primary text-primary-foreground shadow-md shadow-primary/20 translate-x-1"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground hover:pl-4"
              )}
            >
              <item.icon className="mr-3 h-4 w-4 opacity-80 group-hover:opacity-100 transition-opacity" />
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Sidebar Footer (Version/Copyright) - Optional */}
        <div className="p-4 text-xs text-center text-muted-foreground border-t border-border/50">
          Hub Edge Kit v0.1.0
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-full relative overflow-hidden">

        {/* Top Header Bar */}
        <header className="h-16 bg-background/80 backdrop-blur-md border-b border-border flex items-center justify-between px-6 sticky top-0 z-20 shadow-sm">
          {/* Mobile Menu Trigger & Logo (Visible only on mobile) */}
          <div className="md:hidden flex items-center gap-3">
            <img src="/hub-logo-primary.png" alt="Hub" className="h-8 w-auto" />
            <span className="font-bold text-foreground">Edge Kit</span>
          </div>

          {/* Desktop Title / Breadcrumbs (Placeholder) */}
          <div className="hidden md:flex items-center text-muted-foreground text-sm font-medium">
            <span className="text-foreground">Console</span>
            <span className="mx-2">/</span>
            <span>{navItems.find(n => n.path === location.pathname)?.label || 'Overview'}</span>
          </div>

          {/* Right Actions: User Profile & Logout */}
          <div className="flex items-center gap-4">
            <ThemeToggle />

            <div className="h-6 w-px bg-border hidden md:block" />

            {/* User Profile Dropdown / Area */}
            {user ? (
              <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-bold text-foreground leading-none">{user}</p>
                  <p className="text-[10px] uppercase font-bold text-primary mt-1 tracking-wider">Administrator</p>
                </div>
                <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg border border-primary/20 shadow-sm">
                  <CircleUser className="h-5 w-5" />
                </div>

                <button
                  onClick={logout}
                  className="p-2 text-muted-foreground hover:text-destructive transition-colors rounded-full hover:bg-destructive/10"
                  title="Sign Out"
                >
                  <Power className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <button onClick={logout} className="text-sm font-medium text-muted-foreground hover:text-foreground">Sign In</button>
            )}
          </div>
        </header>

        {/* Page Content Container - No padding here, handled by pages */}
        <div ref={scrollRef} data-page-scroll className="flex-1 overflow-y-auto w-full scroll-smooth">
          {children}
        </div>

        <MissingCredentialsModal />
      </main>
    </div>
  );
}
